import { useFieldArray, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form'
import { Botao } from '../../../components/Botao'
import { Cartao } from '../../../components/ListaDeDados'
import { CampoDeTexto } from '../../../components/campos'
import { FORMULARIO } from './textosDoFormulario'
import { LinhaDeItem } from './LinhaDeItem'
import type { FormularioDeOs } from './esquema'

/** Uma linha nova nasce com a fração inteira: 1 de 1. É o caso comum fora dos meses parciais. */
const LINHA_NOVA: FormularioDeOs['itens'][number] = {
  item: '',
  descricao: '',
  metrica: 'VALOR_FIXO',
  unidade: '',
  catser: '',
  valorUnitario: '',
  quantidadeNumerador: 1,
  quantidadeDenominador: 1,
  valorTotalDeclarado: '',
}

interface Props {
  control: Control<FormularioDeOs>
  register: UseFormRegister<FormularioDeOs>
  errors: FieldErrors<FormularioDeOs>
}

/**
 * A seção 4 inteira: as linhas de serviço e o valor total do rodapé.
 *
 * A ordem exige **ao menos uma linha** (`OS_SEM_ITENS` no servidor): uma ordem sem linha autoriza
 * despesa sem dizer de quê. Por isso a última linha não tem botão de remover.
 *
 * O valor total do rodapé fica aqui, e não na identificação, porque é aqui que ele se compara: ele
 * é digitado, a soma das linhas é outra coisa, e a tela da ordem mostra as duas sem corrigir
 * nenhuma. Somar as linhas aqui, no navegador, produziria um terceiro número em IEEE-754 —
 * `Math.round(1.005 * 100) / 100` dá 1,00 onde o servidor dá 1,01.
 *
 * @param props o controle, o registrador e os erros do formulário
 * @returns a seção das linhas
 */
export function CamposDosItens({ control, register, errors }: Props) {
  const { fields, append, remove } = useFieldArray({ control, name: 'itens' })

  return (
    <Cartao titulo={FORMULARIO.secoes.itens}>
      <ul className="space-y-3">
        {fields.map((campo, indice) => (
          <LinhaDeItem
            key={campo.id}
            indice={indice}
            register={register}
            errors={errors}
            aoRemover={fields.length > 1 ? () => remove(indice) : null}
          />
        ))}
      </ul>

      {errors.itens?.message && <p className="mt-2 text-xs font-medium text-red-700">{errors.itens.message}</p>}

      <div className="mt-3">
        <Botao onClick={() => append(LINHA_NOVA)}>{FORMULARIO.item.adicionar}</Botao>
      </div>

      <div className="mt-4 border-t border-slate-200 pt-4 sm:max-w-sm">
        <CampoDeTexto
          rotulo={FORMULARIO.valorTotal}
          ajuda={FORMULARIO.valorTotalAjuda}
          inputMode="decimal"
          erro={errors.valorTotal?.message}
          {...register('valorTotal')}
        />
      </div>
    </Cartao>
  )
}
